import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';

import Vertex from './vertex.glsl';
import Fragment from './fragment.glsl';

const DEV_MODE = false;
const FOCAL_LENGTH = 140;

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const Background = ({
    colour1 = [238, 238, 238],
    colour2 = [238, 238, 238],
    colour3 = [238, 238, 238],
    colour4 = [238, 238, 238],
}) => {
    const containerRef = useRef();

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const scene = new THREE.Scene();

        const geometry = new THREE.PlaneGeometry(1, 1, 300, 300);

        const material = new THREE.ShaderMaterial({
            uniforms: {
                'time': { type: 'f', value: 0.0 },
                'fadeIn': { type: 'f', value: 2.5 },
                'startOffset': { type: 'f', value: 0.0 },
                'screenDimensions': { type: 'f', value: [
                    containerRef.current.clientWidth,
                    containerRef.current.clientHeight
                ]},
                'colours': {
                    value: [
                        new THREE.Color(...colour1.map((c) => c / 255)),
                        new THREE.Color(...colour2.map((c) => c / 255)),
                        new THREE.Color(...colour3.map((c) => c / 255)),
                        new THREE.Color(...colour4.map((c) => c / 255))
                    ]
                }
            },
            vertexShader: Vertex,
            fragmentShader: Fragment,
            side: THREE.DoubleSide,
            blending: THREE.NormalBlending,
            transparent: true,
            // wireframe: DEV_MODE ? true : false
        });

        const plane = new THREE.Mesh(geometry, material);

        plane.rotateX(THREE.MathUtils.degToRad(-30));
        plane.rotateZ(THREE.MathUtils.degToRad(6));

        scene.add(plane);

        const clock = new THREE.Clock();

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setClearColor(0x000000, 0);
        renderer.setSize(window.innerWidth, window.innerHeight);

        const camera = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );
        camera.position.z = 2;
        camera.setFocalLength(FOCAL_LENGTH);

        const start = Math.random() * 100;
        plane.material.uniforms.startOffset.value = start;

        const controls = DEV_MODE ? new OrbitControls(camera, renderer.domElement) : null;

        renderer.setAnimationLoop(() => {
            const canvas = renderer.domElement;
            const needResize = canvas.clientWidth !== containerRef.current.clientWidth || canvas.clientHeight !== containerRef.current.clientHeight;
            if (needResize) {
                camera.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
                camera.setFocalLength(FOCAL_LENGTH);
                camera.updateProjectionMatrix();
                renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
                plane.material.uniforms.screenDimensions.value = [
                    containerRef.current.clientWidth,
                    containerRef.current.clientHeight
                ];
            }

            const delta = clock.getDelta();

            plane.material.uniforms.time.value += delta;

            if (DEV_MODE) {  controls.update(); }

            renderer.render(scene, camera);
        });

        containerRef.current.appendChild(renderer.domElement);

    }, []);

    return (
        <div
            className={`background--container${DEV_MODE ? ' z-10' : ''}`}
            ref={containerRef}
            style={{ backgroundColor: `rgb(${colour1.join(',')})` }} />
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

Background.propTypes = {
    colour1: PropTypes.array,
    colour2: PropTypes.array,
    colour3: PropTypes.array,
    colour4: PropTypes.array
};

export default Background;
